// components/common/ActionMenu/ActionMenu.tsx
"use client";

import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MoreVertical } from "lucide-react";
import "./ActionMenu.css";

interface ActionOption {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
  disabled?: boolean;
  danger?: boolean;
}

interface ActionMenuProps {
  options: ActionOption[];
  position?: "bottom-right" | "bottom-left" | "top-right" | "top-left";
  iconSize?: number;
  menuWidth?: string;
  customTrigger?: React.ReactNode;
  color?: "primary" | "dark" | "secondary" | "success";
}

const ActionMenu: React.FC<ActionMenuProps> = ({
  options,
  position = "bottom-right",
  iconSize = 20,
  menuWidth = "200px",
  customTrigger,
  color = "primary",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  const buttonRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  /* -------------------------------------------------- */
  /* Close on outside click */
  /* -------------------------------------------------- */
  const handleClickOutside = (e: MouseEvent) => {
    if (
      menuRef.current &&
      !menuRef.current.contains(e.target as Node) &&
      buttonRef.current &&
      !buttonRef.current.contains(e.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  /* -------------------------------------------------- */
  /* Position calculation (viewport-safe) */
  /* -------------------------------------------------- */
  useEffect(() => {
    if (!isOpen || !buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const scrollTop = window.scrollY;
    const scrollLeft = window.scrollX;

    const menuW = parseInt(menuWidth, 10);
    const menuH = menuRef.current?.offsetHeight ?? 160;

    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;

    // Initial position (based on prop)
    let top = position.startsWith("top")
      ? rect.top + scrollTop - menuH - 8
      : rect.bottom + scrollTop + 8;

    let left = position.endsWith("left")
      ? rect.left + scrollLeft
      : rect.right + scrollLeft - menuW;

    // Horizontal clamp (keep inside viewport)
    left = Math.max(8, Math.min(left, viewportW - menuW - 8));

    // Vertical flip if overflowing bottom
    if (top + menuH > scrollTop + viewportH) {
      top = rect.top + scrollTop - menuH - 8;
    }

    // Final vertical clamp
    top = Math.max(scrollTop + 8, top);

    setCoords({ top, left });
  }, [isOpen, position, menuWidth]);

  /* -------------------------------------------------- */
  /* Outside click listener */
  /* -------------------------------------------------- */
  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  /* -------------------------------------------------- */
  /* Menu JSX (unchanged behavior) */
  /* -------------------------------------------------- */
  const menu = (
    <div
      ref={menuRef}
      className="action-menu-list"
      style={{
        top: coords.top,
        left: coords.left,
        width: menuWidth,
      }}
    >
      {options.map((option, index) => (
        <button
          key={index}
          className={`action-menu-item ${option.danger ? "danger" : ""} ${
            option.disabled ? "disabled" : ""
          }`}
          onClick={(e) => {
            e.stopPropagation();
            if (!option.disabled) {
              option.onClick();
              setIsOpen(false);
            }
          }}
          disabled={option.disabled}
        >
          {option.icon && (
            <span className="action-menu-icon">{option.icon}</span>
          )}
          <span className="action-menu-label">{option.label}</span>
        </button>
      ))}
    </div>
  );
 
  /* -------------------------------------------------- */
  /* Render */
  /* -------------------------------------------------- */
  return (
    <>
      <div
        ref={buttonRef}
        className={`action-menu-trigger color-${color}`}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen((prev) => !prev);
        }}
      >
        {customTrigger || <MoreVertical size={iconSize} />}
      </div>

      {isOpen && typeof window !== "undefined"
        ? createPortal(menu, document.body)
        : null}
    </>
  );
};

export default ActionMenu;
